/**
 * FullVSL watched-progress delayed CTA + delayed page elements (FUL-9).
 *
 * Core product rule (PRD §3.2, common.md): the CTA reveals on *watched*
 * progress — i.e. the greatest contiguous position the visitor actually
 * watched (`maxWatched`) — not on simple page-time. Rewinding therefore cannot
 * re-hide a revealed CTA when `persistAfterReveal` is set (the CTO default).
 *
 * This module is split into:
 *  - Pure evaluation helpers (unit-tested, no DOM): threshold math, reveal
 *    decision, delayed-element due checks, destination resolution.
 *  - `CtaController`: owns the CTA overlay DOM, drives show/hide, fires the
 *    `cta_shown` / `cta_clicked` player events through a sink, and toggles
 *    delayed page elements by CSS selector.
 *
 * Event names follow the FUL-5 vocabulary (`cta_shown`, `cta_clicked`). FUL-6
 * naming is still in-flight, so the dispatcher is left stubbed by default — the
 * player passes an `emit` sink that is a no-op unless `onEvent` is configured.
 */

import type {
  CtaConfig,
  CtaDestinationType,
  DelayedElement,
  PlayerCtaEvent,
} from "../../contracts/src/contracts";
import type { PlayerMediaLike } from "./player-media";

/** Watched-progress snapshot handed to the reveal evaluator. */
export interface WatchProgress {
  /** Current playback clock position, seconds. */
  currentTimeSec: number;
  /** Greatest contiguous position actually watched, seconds (monotonic max). */
  maxWatchedSec: number;
  /** Media duration, seconds (0 while unknown). */
  durationSec: number;
}

/**
 * Largest single `timeupdate` delta treated as natural playback. A jump bigger
 * than this between two updates is a user seek/scrub, so the gap is not credited
 * to `maxWatched` (anti-gaming against forward-skipping the CTA into view).
 */
export const NATURAL_STEP_SEC = 0.75;

/**
 * Advance the watched high-water mark. Only natural playback steps raise it; a
 * forward jump (seek) does not credit the skipped span. Backward movement never
 * lowers it. Returns the new maxWatched value.
 */
export function advanceMaxWatched(
  prevMax: number,
  currentTimeSec: number,
  prevCurrentTimeSec: number,
): number {
  if (!(currentTimeSec >= 0)) return prevMax;
  const jump = currentTimeSec - prevCurrentTimeSec;
  const isNatural = jump <= NATURAL_STEP_SEC;
  if (currentTimeSec > prevMax && isNatural) return currentTimeSec;
  return prevMax;
}

/** Clamp a number into [min, max]. */
function clamp(v: number, min: number, max: number): number {
  if (v < min) return min;
  if (v > max) return max;
  return v;
}

/**
 * Resolve the absolute seconds threshold at which a CTA config triggers.
 * `percent` maps against duration (guarded against zero/NaN duration); a
 * `timestamp` reveal is used verbatim.
 */
export function ctaThresholdSeconds(cta: CtaConfig, durationSec: number): number {
  if (cta.revealMode === "percent") {
    if (!(durationSec > 0) || !Number.isFinite(durationSec)) return Number.POSITIVE_INFINITY;
    return (clamp(cta.revealAt, 0, 100) / 100) * durationSec;
  }
  return Math.max(0, cta.revealAt);
}

export interface CtaRevealDecision {
  shouldShow: boolean;
  shouldHide: boolean;
}

/**
 * Decide whether the CTA should show or hide for the current watched progress.
 *
 * - `basedOn: "maxWatched"` (default): reveals once watched progress crosses the
 *   threshold and stays revealed through rewinds (anti-gaming). This is the
 *   headline behavior for FUL-9.
 * - `basedOn: "currentTime"`: reveals based on the live playhead.
 * - `rehideOnRewind`: when the metric drops back below threshold, hide again.
 */
export function evaluateCtaReveal(
  cta: CtaConfig,
  progress: WatchProgress,
  currentlyVisible: boolean,
): CtaRevealDecision {
  const threshold = ctaThresholdSeconds(cta, progress.durationSec);
  const metric = cta.basedOn === "maxWatched" ? progress.maxWatchedSec : progress.currentTimeSec;
  const reached = Number.isFinite(threshold) && metric >= threshold;
  if (reached) return { shouldShow: true, shouldHide: false };
  if (currentlyVisible && cta.rehideOnRewind) return { shouldShow: false, shouldHide: true };
  return { shouldShow: false, shouldHide: false };
}

/**
 * Whether a delayed page element is due *now* on a watched-progress trigger
 * (`timestamp` / `percent`). Event-style triggers (`ctaClick`, `pause`,
 * `complete`) are handled imperatively by the controller and return false here.
 */
export function delayedElementDue(el: DelayedElement, progress: WatchProgress): boolean {
  if (el.trigger === "timestamp") return progress.currentTimeSec >= el.at;
  if (el.trigger === "percent") {
    return progress.durationSec > 0 && (progress.currentTimeSec / progress.durationSec) * 100 >= el.at;
  }
  return false;
}

/** Normalize a destination type to a coarse behavior bucket. */
export function destinationBehavior(type: CtaDestinationType): "navigate" | "js_event" | "css_reveal" {
  if (type === "js_event") return "js_event";
  if (type === "css_reveal") return "css_reveal";
  return "navigate";
}

const CTA_CSS = `
.fvsl-cta{position:absolute;left:50%;bottom:14%;transform:translate(-50%,8px);z-index:3;max-width:86%;opacity:0;pointer-events:none;transition:opacity .25s ease,transform .25s ease;display:flex;flex-direction:column;align-items:center;gap:8px}
.fvsl-cta.is-visible{opacity:1;pointer-events:auto;transform:translate(-50%,0)}
.fvsl-cta-card{background:rgba(8,12,16,.82);border:1px solid rgba(107,216,203,.35);border-radius:12px;padding:12px 16px;color:#fff;font:14px/1.35 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;text-align:center;box-shadow:0 12px 30px rgba(0,0,0,.45);backdrop-filter:blur(6px)}
.fvsl-cta-headline{font-weight:700;font-size:16px;margin:0 0 4px}
.fvsl-cta-sub{margin:0;opacity:.85;font-size:13px}
.fvsl-cta-btn{appearance:none;border:0;border-radius:10px;background:var(--fvsl-brand,#00685f);color:#fff;font:600 15px/1 system-ui,-apple-system,sans-serif;padding:12px 22px;cursor:pointer;transition:filter .12s ease,transform .12s ease;text-decoration:none;display:inline-block}
.fvsl-cta-btn:hover{filter:brightness(1.12)}
.fvsl-cta-btn:active{transform:translateY(1px)}
.fvsl-cta-close{position:absolute;top:-10px;right:-10px;width:24px;height:24px;border-radius:50%;border:0;background:rgba(0,0,0,.7);color:#fff;font-size:15px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center}
.fvsl-cta-close:hover{background:rgba(0,0,0,.9)}
@media (max-width:640px){.fvsl-cta{bottom:18%}.fvsl-cta-btn{padding:11px 18px;font-size:14px}}
`;

let ctaCssInjected = false;
function injectCtaCss(): void {
  if (ctaCssInjected) return;
  ctaCssInjected = true;
  const style = document.createElement("style");
  style.setAttribute("data-fullvsl-cta", "");
  style.textContent = CTA_CSS;
  document.head.appendChild(style);
}

/** Default CTA copy when the config carries no content. */
const DEFAULT_HEADLINE = "Aproveite esta oferta";
const DEFAULT_BUTTON = "Acessar agora";

export interface CtaControllerOptions {
  /** Media element — HTML5 <video> or YouTube IFrame adapter (PlayerMediaLike). */
  media: PlayerMediaLike;
  /** Mount layer inside the player frame (above media, below controls). */
  layer: HTMLElement;
  config: CtaConfig;
  delayedElements?: DelayedElement[];
  /** Optional editable copy; falls back to utilitarian defaults. */
  headline?: string;
  subtext?: string;
  buttonLabel?: string;
  /** Emit a `cta_shown` / `cta_clicked` player event (player owns seq/identity). */
  emit: (event: PlayerCtaEvent) => void;
  /**
   * FUL-107 (IFRAME-3): resolve a navigate destination. Returns true when a
   * window/tab was actually opened. When omitted (or when this returns false),
   * the controller falls back to its own `window.open` and finally to the
   * `onNavigateBlocked` hook so the player can bridge via postMessage.
   */
  onNavigate?: (url: string) => boolean;
  /** Called when navigation could not open a window (host bridges via postMessage). */
  onNavigateBlocked?: (url: string) => void;
  /**
   * FUL-107 (IFRAME-10): a delayed element was triggered. Called after the
   * in-iframe toggle attempt so the player can also bridge the selector to the
   * host page (which the iframe cannot reach cross-origin).
   */
  onDelayedReveal?: (selector: string) => void;
  /**
   * FUL-107 (IFRAME-10): a `js_event` CustomEvent destination fired. The
   * controller still dispatches it in-iframe; this lets the player bridge it to
   * the host so listeners on the host document also receive it.
   */
  onCustomEvent?: (name: string, detail: unknown) => void;
}

/**
 * Owns CTA reveal lifecycle and delayed page-element toggling for one player.
 * The player feeds it `WatchProgress` on each progress tick and notifies it of
 * pause/complete/click moments.
 */
export class CtaController {
  private readonly cfg: CtaConfig;
  private readonly media: PlayerMediaLike;
  private readonly layer: HTMLElement;
  private readonly delayed: DelayedElement[];
  private readonly emit: (e: PlayerCtaEvent) => void;
  private readonly onNavigate?: (url: string) => boolean;
  private readonly onNavigateBlocked?: (url: string) => void;
  private readonly onDelayedReveal?: (selector: string) => void;
  private readonly onCustomEvent?: (name: string, detail: unknown) => void;
  private readonly card: HTMLElement;
  private readonly btn: HTMLAnchorElement;
  private readonly closeBtn: HTMLButtonElement;
  private visible = false;
  private userClosed = false;
  private readonly shownDelayed = new Set<number>();

  constructor(opts: CtaControllerOptions) {
    injectCtaCss();
    this.cfg = opts.config;
    this.media = opts.media;
    this.layer = opts.layer;
    this.delayed = opts.delayedElements ?? [];
    this.emit = opts.emit;
    this.onNavigate = opts.onNavigate;
    this.onNavigateBlocked = opts.onNavigateBlocked;
    this.onDelayedReveal = opts.onDelayedReveal;
    this.onCustomEvent = opts.onCustomEvent;

    const wrap = document.createElement("div");
    wrap.className = "fvsl-cta";
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-hidden", "true");

    const card = document.createElement("div");
    card.className = "fvsl-cta-card";

    const head = document.createElement("p");
    head.className = "fvsl-cta-headline";
    head.textContent = opts.headline || DEFAULT_HEADLINE;

    const sub = document.createElement("p");
    sub.className = "fvsl-cta-sub";
    sub.textContent = opts.subtext || "";
    sub.hidden = !opts.subtext;

    const btn = document.createElement("a");
    btn.className = "fvsl-cta-btn";
    btn.textContent = opts.buttonLabel || DEFAULT_BUTTON;
    btn.href = "#";

    card.appendChild(head);
    card.appendChild(sub);
    card.appendChild(btn);

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "fvsl-cta-close";
    closeBtn.setAttribute("aria-label", "Fechar oferta");
    closeBtn.textContent = "×";

    wrap.appendChild(closeBtn);
    wrap.appendChild(card);
    this.layer.appendChild(wrap);

    this.card = wrap;
    this.btn = btn;
    this.closeBtn = closeBtn;

    btn.addEventListener("click", this.onButtonClick);
    closeBtn.addEventListener("click", this.onClose);
  }

  /** Drive reveal evaluation from the player's progress tick. */
  update(progress: WatchProgress): void {
    if (this.userClosed && this.cfg.persistAfterReveal) {
      this.evaluateDelayed(progress);
      return;
    }
    const decision = evaluateCtaReveal(this.cfg, progress, this.visible);
    if (decision.shouldShow && !this.visible) this.show(progress);
    else if (decision.shouldHide && this.visible) this.hide();
    this.evaluateDelayed(progress);
  }

  /** Reveal the CTA and emit `cta_shown`. */
  show(progress: WatchProgress): void {
    if (this.visible) return;
    this.visible = true;
    this.applyDestinationHref();
    this.card.classList.add("is-visible");
    this.card.setAttribute("aria-hidden", "false");
    this.emit(this.buildEvent("cta_shown", progress));
  }

  /** Hide the CTA (used by rehideOnRewind or user dismissal). */
  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.card.classList.remove("is-visible");
    this.card.setAttribute("aria-hidden", "true");
  }

  /** Fire `cta_clicked` and resolve the configured destination. */
  private onButtonClick = (ev: MouseEvent): void => {
    ev.preventDefault();
    const progress = this.snapshot();
    this.emit(this.buildEvent("cta_clicked", progress));
    this.resolveDestination();
    // ctaClick-triggered delayed elements fire on click.
    this.delayed.forEach((el, i) => {
      if (el.trigger === "ctaClick") this.revealDelayed(i, el);
    });
  };

  private onClose = (): void => {
    this.userClosed = true;
    this.hide();
  };

  /** Notify pause-triggered delayed elements. */
  notifyPause(): void {
    this.delayed.forEach((el, i) => {
      if (el.trigger === "pause") this.revealDelayed(i, el);
    });
  }

  /** Notify completion-triggered delayed elements. */
  notifyComplete(): void {
    this.delayed.forEach((el, i) => {
      if (el.trigger === "complete") this.revealDelayed(i, el);
    });
  }

  get isVisible(): boolean {
    return this.visible;
  }

  destroy(): void {
    this.btn.removeEventListener("click", this.onButtonClick);
    this.closeBtn.removeEventListener("click", this.onClose);
    this.card.remove();
  }

  private evaluateDelayed(progress: WatchProgress): void {
    this.delayed.forEach((el, i) => {
      if (this.shownDelayed.has(i)) return;
      if (el.trigger === "timestamp" || el.trigger === "percent") {
        if (delayedElementDue(el, progress)) this.revealDelayed(i, el);
      }
    });
  }

  private revealDelayed(index: number, el: DelayedElement): void {
    if (this.shownDelayed.has(index)) return;
    this.shownDelayed.add(index);
    try {
      const nodes = document.querySelectorAll(el.cssSelector);
      nodes.forEach((n) => {
        const html = n as HTMLElement;
        html.hidden = false;
        html.style.removeProperty("display");
      });
    } catch {
      // selector errors are non-fatal — host page may not contain the element.
    }
    // IFRAME-10: a cross-origin iframe cannot reach host-page DOM, so also ask
    // the host (via the player bridge) to reveal the same selector.
    this.onDelayedReveal?.(el.cssSelector);
  }

  private applyDestinationHref(): void {
    const behavior = destinationBehavior(this.cfg.destination.type);
    if (behavior === "navigate") {
      this.btn.href = this.cfg.destination.value || "#";
      this.btn.target = "_blank";
      this.btn.rel = "noopener noreferrer";
    } else {
      this.btn.href = "#";
    }
  }

  private resolveDestination(): void {
    const dest = this.cfg.destination;
    const behavior = destinationBehavior(dest.type);
    if (behavior === "navigate") {
      const url = dest.value;
      if (url) {
        // Prefer the player-supplied resolver (knows the bridge + sandbox state);
        // fall back to window.open, then signal a block so the host can bridge.
        const opened = this.onNavigate ? this.onNavigate(url) : this.tryWindowOpen(url);
        if (!opened) this.onNavigateBlocked?.(url);
      }
      return;
    }
    if (behavior === "css_reveal") {
      try {
        document.querySelectorAll(dest.value).forEach((n) => {
          const html = n as HTMLElement;
          html.hidden = false;
          html.style.removeProperty("display");
        });
      } catch {
        /* non-fatal */
      }
      // IFRAME-10: bridge the selector to the host as well (css_reveal destinations
      // typically target host-page nodes the iframe cannot reach).
      this.onDelayedReveal?.(dest.value);
      return;
    }
    // js_event
    const name = dest.value || "fullvsl:cta";
    try {
      window.dispatchEvent(new CustomEvent(name, { detail: { destination: dest } }));
    } catch {
      /* non-fatal */
    }
    // IFRAME-10: CustomEvents do not cross the iframe boundary — re-dispatch on host.
    this.onCustomEvent?.(name, { destination: dest });
  }

  /** Attempt window.open; returns false when blocked (sandbox without allow-popups). */
  private tryWindowOpen(url: string): boolean {
    try {
      const handle = window.open(url, "_blank", "noopener,noreferrer");
      return !!handle;
    } catch {
      return false;
    }
  }

  private snapshot(): WatchProgress {
    const dur = this.media.duration;
    return {
      currentTimeSec: this.media.currentTime,
      maxWatchedSec: this.media.currentTime,
      durationSec: Number.isFinite(dur) ? dur : 0,
    };
  }

  private buildEvent(
    name: "cta_shown" | "cta_clicked",
    progress: WatchProgress,
  ): PlayerCtaEvent {
    // seq/clientTs/identity are filled by the player's emit wrapper; here we
    // only carry the CTA-specific payload. The player merges base fields.
    return {
      seq: 0,
      name,
      clientTs: new Date().toISOString(),
      vslId: "",
      sessionId: "",
      visitorId: "",
      revealMode: this.cfg.revealMode,
      basedOn: this.cfg.basedOn,
      positionSec: progress.currentTimeSec,
      destinationType: this.cfg.destination.type,
    };
  }
}
